import React, { useState, useRef, useEffect } from "react";
import Collapse from "@mui/material/Collapse";
import { BsChevronDown, BsChevronUp } from "react-icons/bs";
// mui
import { useTheme } from "@mui/styles";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
// local components
import scroller from "lib/scroller";

export default function TableOfContent({ TOCData, customHeading, mode }) {
  const theme = useTheme();
  const [activeID, setActiveID] = useState();
  const [TOCDataState, setTOCDataState] = useState(TOCData);
  const [TOCExpanded, setTOCExpanded] = useState(
    mode === "search" ? true : false
  );
  const [searchKeyword, setSearchKeyword] = useState("");
  const headingElementsRef = useRef({});

  useEffect(() => {
    const callback = (headings) => {
      headingElementsRef.current = headings.reduce((map, headingElement) => {
        map[headingElement.target.id] = headingElement;
        return map;
      }, headingElementsRef.current);

      const visibleHeadings = [];
      Object.keys(headingElementsRef.current).forEach((key) => {
        const headingElement = headingElementsRef.current[key];
        if (headingElement.isIntersecting) visibleHeadings.push(headingElement);
      });

      const getIndexFromId = (id) =>
        headingElements.findIndex((heading) => heading.id === id);

      if (visibleHeadings.length === 1) {
        setActiveID(visibleHeadings[0].target.id);
      } else if (visibleHeadings.length > 1) {
        const sortedVisibleHeadings = visibleHeadings.sort(
          (a, b) => getIndexFromId(a.target.id) > getIndexFromId(b.target.id)
        );

        setActiveID(sortedVisibleHeadings[0].target.id);
      }
    };

    const observer = new IntersectionObserver(callback, {
      rootMargin: "0px 0px -10% 0px",
    });

    const headingElements = Array.from(document.querySelectorAll("h2"));

    headingElements.forEach((element) => observer.observe(element));

    return () => observer.disconnect();
  }, [setActiveID]);

  useEffect(() => {
    // SearchKeyword
    if (searchKeyword) {
      const updatedTOCData = TOCData.slice();

      const filteredTOCData = updatedTOCData.filter((item) => {
        return item.title.toLowerCase().includes(searchKeyword.toLowerCase());
      });

      setTOCDataState(filteredTOCData);
    } else {
      setTOCDataState(TOCData);
    }
  }, [searchKeyword]);

  return (
    <>
      <Box
        sx={{
          display: "none",
          [theme.breakpoints.down("md")]: {
            display: "flex",
            flexDirection: "column",
            width: "100%",
            backgroundColor: "white",
          },
        }}
      >
        {mode !== "search" && (
          <div
            style={{
              display: "flex",
              flexDirection: "row",
              justifyContent: "space-between",
            }}
            onClick={() => {
              setTOCExpanded(!TOCExpanded);
            }}
          >
            <div style={{ marginLeft: "10px", fontSize: "25px" }}>
              {customHeading ? customHeading : "Table of contents"}
            </div>
            <div style={{ margin: "auto 10px" }}>
              {TOCExpanded ? (
                <BsChevronUp size={25} color={process.env.primaryBrandColor} />
              ) : (
                <BsChevronDown
                  size={25}
                  color={process.env.primaryBrandColor}
                />
              )}
            </div>
          </div>
        )}
        {mode === "search" && ( // note this is in 2 places because of the historical layout of this component between desktop and mobile
          <TextField
            sx={{ margin: theme.spacing(1, 0, 1, 0) }}
            label="Search"
            variant="outlined"
            onChange={(event) => {
              setSearchKeyword(event.target.value);
            }}
          />
        )}
        <Collapse
          in={TOCExpanded}
          sx={{
            [theme.breakpoints.down("md")]: {
              overflow: "hidden",
              padding: theme.spacing(0, 1),
              width: "100%",
              display: "flex",
              flexDirection: "column",
              backgroundColor: "white !important",
              borderRadius: theme.spacing(0.5),
              boxShadow:
                "0 1px 3px rgb(0 0 0 / 12%), 0 1px 2px rgb(0 0 0 / 24%)",
            },
          }}
        >
          {TOCDataState.map((TOCItem, index) => {
            return (
              <Button
                sx={{
                  width: "100%",
                  textTransform: "none",
                  fontSize: "16px",
                  cursor: "pointer",
                  textDecoration: "none",
                  margin: theme.spacing(0.5, 0, 0.5, 2),
                  padding: theme.spacing(0, 0, 0, 2),
                  // padding: theme.spacing(0, 4),
                  borderLeft: "3px solid rgba(96, 97, 101, 0.2)",
                  opacity: 1,
                  color: "gray",
                  fontWeight: 300,
                  borderRadius: "0px",
                  textAlign: "left !important",
                  display: "block !important",
                  borderLeftColor:
                    activeID === TOCItem.id
                      ? process.env.primaryBrandColorLight
                      : "inherit",
                  color:
                    activeID === TOCItem.id
                      ? process.env.primaryBrandColorLight
                      : "inherit",
                  fontWeight: activeID === TOCItem.id ? 600 : "inherit",
                }}
                onClick={() => {
                  const id = TOCItem.id;
                  scroller(id);
                }}
              >
                {TOCItem.title}
              </Button>
            );
          })}
        </Collapse>
      </Box>

      <Box
        sx={{
          [theme.breakpoints.down("md")]: {
            display: "none",
          },
        }}
      >
        <Box
          sx={{
            fontSize: "26px",
            fontWeight: "600",
          }}
        >
          {customHeading ? customHeading : "Table of contents"}
        </Box>
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
          }}
        >
          {mode === "search" && (
            <TextField
              sx={{ margin: theme.spacing(1, 0, 1, 0) }}
              label="Search"
              variant="outlined"
              onChange={(event) => {
                setSearchKeyword(event.target.value);
              }}
            />
          )}
          {TOCDataState.map((TOCItem) => {
            return (
              <Button
                sx={{
                  textTransform: "none",
                  fontSize: "16px",
                  cursor: "pointer",
                  textDecoration: "none",
                  margin: theme.spacing(0, 0, 0, 0),
                  padding: theme.spacing(0, 0, 0, 2),
                  borderLeft: "3px solid rgba(96, 97, 101, 0.2)",
                  opacity: 1,
                  color: "gray",
                  fontWeight: 300,
                  borderRadius: "0px",
                  textAlign: "left !important",
                  display: "block !important",
                  [theme.breakpoints.down("md")]: {},
                  "&:hover": {
                    color: process.env.primaryBrandColorLight,
                  },
                  borderLeftColor:
                    activeID === TOCItem.id
                      ? process.env.primaryBrandColorLight
                      : "inherit",
                  color:
                    activeID === TOCItem.id
                      ? process.env.primaryBrandColorLight
                      : "inherit",
                  fontWeight: activeID === TOCItem.id ? 600 : "inherit",
                }}
                onClick={() => {
                  const id = TOCItem.id;
                  // const yOffset = -150;
                  // const element = document.getElementById(id);

                  // const y = element.offsetTop + yOffset;

                  // setTimeout(() => {
                  //   document.body.scrollTo({ top: y, behavior: "smooth" });
                  // }, 100);

                  scroller(id);
                }}
              >
                {TOCItem.title}
              </Button>
            );
          })}
        </Box>
      </Box>
    </>
  );
}
